import { baseUrl } from '../../../baseUrl';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { isValidEmail } from '../../../utils/emailValidator';
import { fetchWithAuthHandling } from '../../../authHandling';
import { useState, type KeyboardEvent, type ClipboardEvent, type ChangeEvent } from 'react';
import { Alert, Button, CircularProgress, Container, TextField, Typography } from '@mui/material';

export const Route = createFileRoute('/_app/referrals/')({
    component: RouteComponent,
})

function RouteComponent() {
    const [emailInput, setEmailInput] = useState("");
    const [emails, setEmails] = useState<string[]>([]);
    const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
    const [duplicateEmails, setDuplicateEmails] = useState<string[]>([]);
    const [submissionError, setSubmissionError] = useState<string | null>(null);

    const mutation = useMutation({
        mutationFn: async () => {
            const buyers = emails.map(email => ({ emailAddress: email }));

            const response = await fetchWithAuthHandling(`${baseUrl}/referrals/bulk-create`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ buyers }),
            });

            if (!response.ok) {
                throw new Error(`Failed to send emails: ${response.statusText}`);
            }

            return response;
        },
        onSuccess: () => {
            setEmails([]);
            setEmailInput("");
            setDuplicateEmails([]);
            setInvalidEmails([]);
            setSubmissionError(null);
        },
        onError: (error) => {
            setSubmissionError(error instanceof Error ? error.message : "An unknown error occurred");
        },
    });

    const processEmails = (input: string) => {
        const emailArray = input
            .split(/[\n, ]+/)
            .map(email => email.trim())
            .filter(email => email.length > 0);

        const validEmails = emailArray.filter(isValidEmail);
        const invalidEmails = emailArray.filter(email => !isValidEmail(email));

        const uniqueEmails = [...new Set(validEmails)];
        const duplicates = emailArray.filter(
            (email, index) => emailArray.indexOf(email) !== index && isValidEmail(email)
        );

        setEmails(uniqueEmails);
        setInvalidEmails(invalidEmails);
        setDuplicateEmails([...new Set(duplicates)]);

        setEmailInput(input);
    };


    const handleSubmit = () => {
        processEmails(emailInput);

        if (invalidEmails.length > 0) {
            setSubmissionError(`Invalid email(s) detected: ${invalidEmails.join(", ")}`);
            return;
        }

        if (emails.length > 0) {
            mutation.mutate();
        }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEmailInput(event.target.value);
        processEmails(event.target.value);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            processEmails(emailInput);
        }
    };


    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const pastedText = event.clipboardData.getData("text");
        processEmails(pastedText);
    };


    return (
        <Container
            sx={{
                mt: 3,
                maxWidth: "97%",
                display: "flex",
                flexDirection: "column",
            }}
            disableGutters
        >
            <Typography variant="h6" gutterBottom>
                Enter Email Addresses:
            </Typography>

            <TextField
                fullWidth
                multiline
                rows={15}
                placeholder="Enter emails separated by commas, spaces, or new lines..."
                variant="outlined"
                value={emailInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                error={invalidEmails.length > 0 || duplicateEmails.length > 0}
                helperText={
                    invalidEmails.length > 0
                        ? `Invalid email(s): ${invalidEmails.join(", ")}`
                        : duplicateEmails.length > 0
                            ? `Duplicate email(s) removed: ${duplicateEmails.join(", ")}`
                            : ""
                }
                sx={{
                    borderRadius: 2,
                }}
            />

            {submissionError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {submissionError}
                </Alert>
            )}

            {mutation.status === "pending" && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} /> Sending emails...
                </Alert>
            )}

            {mutation.status === "success" && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    Emails successfully sent!
                </Alert>
            )}

            <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2, width: "100%", fontSize: "1rem", padding: "10px" }}
                onClick={handleSubmit}
                disabled={mutation.status === "pending" || emails.length === 0 || invalidEmails.length > 0}
            >
                {mutation.status === "pending" ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Submit Emails"}
            </Button>

        </Container>
    );
}